import { Component, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthService, SystemsService } from '@twaice-fe/frontend/shared/services';
import { actions, selectors } from '@twaice-fe/frontend/shared/store';
import {
  ANALYTICS_UPSELL_ROUTE,
  ENERGY_ALERTS_ROUTE,
  ENERGY_BASE_ROUTE,
  ENERGY_PENALTY_RISK_ROUTE,
  ENERGY_PERFORMANCE_MANAGER_ROUTE,
  ENERGY_SAFETY_ROUTE,
  ENERGY_STORAGE_OVERVIEW_ROUTE,
  ENERGY_WARRANTY_TRACKER_ROUTE,
  MOBILITY_BASE_ROUTE,
  REPORTING_ROUTE,
} from '@twaice-fe/shared/constants';
import { CustomerType, Solution, User } from '@twaice-fe/shared/models';
import { NzModalService } from 'ng-zorro-antd/modal';
import { Observable } from 'rxjs';
import { SettingsComponent } from '../../../views/settings/settings.component';
import { ToolbarService } from './toolbar.service';

const { configsAction } = actions;
const { configsSelectors } = selectors;

@Component({
  selector: 'twaice-fe-toolbar',
  templateUrl: './toolbar.component.html',
  styleUrls: ['./toolbar.component.scss'],
  standalone: false,
  providers: [ToolbarService],
})
export class ToolbarComponent {
  loggedInUser$: Observable<User>;
  username: string;

  title: string;
  showAnalyticsUpsell = false;
  intercomTarget = 'dashboard-layout-toolbar';

  isDemoEnabled = true;
  useCustomerNaming = true;

  protected logoUrl$: Observable<string | null>;

  constructor(
    private destroy$: DestroyRef,
    private systemsService: SystemsService,
    private router: Router,
    protected authService: AuthService,
    private toolbarService: ToolbarService,
    protected store: Store,

    private modalService: NzModalService
  ) {
    this.loggedInUser$ = this.authService.getUserObservable();

    this.store
      .select(configsSelectors.getCustomerType)
      .pipe(takeUntilDestroyed())
      .subscribe((customerType) => {
        switch (customerType) {
          case CustomerType.ENERGY:
            this.title = 'BESS Analytics';
            return;
          case CustomerType.MOBILITY:
            this.title = 'E-mobility Analytics Suite';
            return;
          case CustomerType.MODELS:
            this.title = 'Analytics Suite';
            this.showAnalyticsUpsell = true;
            return;
        }
      });

    this.store
      .select(configsSelectors.getAvailableSolutionList)
      .pipe(takeUntilDestroyed())
      .subscribe((solutions) => {
        if (solutions.includes(Solution.ENERGY) && !solutions.includes(Solution.MODEL_LIBRARY)) {
          this.isDemoEnabled = true;
        } else {
          this.isDemoEnabled = false;
        }
      });

    this.store
      .select(configsSelectors.getUseCustomerNaming)
      .pipe(takeUntilDestroyed())
      .subscribe((useCustomerNaming) => (this.useCustomerNaming = useCustomerNaming));

    this.logoUrl$ = this.toolbarService.getLogoUrl$();
  }

  /**
   * Logs the user out, resets configuration, and navigates to the login page.
   */
  async logout() {
    this.systemsService.resetConfig();
    await this.authService.logout();
  }

  navigateToBaseUrl() {
    this.store
      .select(configsSelectors.getBaseUrl)
      .pipe(takeUntilDestroyed(this.destroy$))
      .subscribe((baseUrl) =>
        // eslint-disable-next-line twaice/no-router-navigate-outside-effects
        this.router.navigate([this.showAnalyticsUpsell ? '/analytics' : baseUrl], { queryParamsHandling: 'preserve' })
      );
  }

  openHelpCenter(): void {
    window.open('https://help.twaice.cloud', '_blank');
  }

  baseUrlActive() {
    return (
      this.router.url.includes(ENERGY_BASE_ROUTE) ||
      this.router.url.includes(ENERGY_PERFORMANCE_MANAGER_ROUTE) ||
      this.router.url.includes(ENERGY_WARRANTY_TRACKER_ROUTE) ||
      this.router.url.includes(ENERGY_ALERTS_ROUTE) ||
      this.router.url.includes(ENERGY_SAFETY_ROUTE) ||
      this.router.url.includes(ENERGY_PENALTY_RISK_ROUTE) ||
      this.router.url.includes(ENERGY_STORAGE_OVERVIEW_ROUTE) ||
      this.router.url.includes(REPORTING_ROUTE) ||
      this.router.url.includes(ANALYTICS_UPSELL_ROUTE) ||
      this.router.url.includes(MOBILITY_BASE_ROUTE)
    );
  }

  switchCustomerNaming() {
    this.store.dispatch(configsAction.useCustomerNamingChanged({ useCustomerNaming: !this.useCustomerNaming }));
  }

  openSettingsModal(): void {
    this.modalService.create({
      nzContent: SettingsComponent,
      nzClosable: true,
      nzFooter: null,
      nzWidth: 800,
      nzTitle: 'Account Settings',
      nzCentered: true,
      nzBodyStyle: { padding: '16px' },
    });
  }
}
